import { DataGrid, type GridColumn } from "@/components/data-grid";
import { EmptyState } from "@/components/EmptyState";
import { ParticipantsIcon, PlusIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { errorMessage } from "../lib/query.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { useDashboardPeriod } from "../app/DashboardShell.tsx";
import { useSession } from "../app/session.tsx";
import { fromPeriod, monthLabel, MONTHS, parseYear } from "./calendar.ts";
import { catalogQuery, POSITIONS, REGIONS, type Catalog } from "./catalog.ts";
import {
  activeLabel,
  agencyLabel,
  ALL,
  EMPTY_FILTERS,
  participantQuery,
  participantRecord,
  participantsQuery,
  toFilters,
  useEnrollParticipant,
  useSetParticipantActive,
  type EnrollmentView,
  type FilterControls,
  type ParticipantDetail,
  type ParticipantSummary,
} from "./participants.ts";

/** Lo que se espera a que el usuario deje de teclear antes de preguntar. */
const SEARCH_DELAY_MS = 250;

/**
 * Fuera del componente: si se declararan dentro, cambiarían de identidad en
 * cada render y la rejilla se reconstruiría entera.
 *
 * En la ficha, el año y el mes son DOS columnas y no una. Con el periodo
 * compuesto en una sola, ordenar era ordenar texto: bastaba con que un mes
 * llegara sin cero delante para que noviembre se colara antes que febrero.
 */
const PARTICIPANT_COLUMNS: readonly GridColumn<ParticipantSummary>[] = [
  { key: "fullName", label: "Nombre", render: (p) => p.fullName },
  {
    key: "identifier",
    label: "Identificador",
    render: (p) => <span className="font-mono">{p.identifier}</span>,
  },
  {
    key: "active",
    label: "Estado",
    render: (p) => <StateTag active={p.active} />,
    sortValue: (p) => p.active,
  },
  {
    key: "position",
    label: "Cargo",
    render: (p) => p.position?.name ?? "—",
    sortValue: (p) => p.position?.name ?? null,
  },
  // Sin columnas de año y mes: la lista es la de UN mes --el del selector de
  // arriba--, así que repetirlo en cada fila no dice nada. En la ficha sí
  // están, porque allí lo que se lee es el histórico.
  {
    key: "region",
    label: "Región",
    render: (p) => p.region?.name ?? "—",
    sortValue: (p) => p.region?.name ?? null,
  },
  {
    key: "agency",
    label: "Vinculación",
    render: (p) => agencyLabel(p.agency),
    sortValue: (p) => p.agency,
  },
];

const ENROLLMENT_COLUMNS: readonly GridColumn<EnrollmentView>[] = [
  { key: "year", label: "Año", numeric: true, render: (e) => e.year },
  {
    key: "month",
    label: "Mes",
    render: (e) => monthLabel(e.month),
    sortValue: (e) => e.month,
  },
  {
    key: "position",
    label: "Cargo",
    render: (e) => e.position.name,
    sortValue: (e) => e.position.name,
  },
  {
    key: "region",
    label: "Región",
    render: (e) => e.region?.name ?? "—",
    sortValue: (e) => e.region?.name ?? null,
  },
  {
    key: "agency",
    label: "Vinculación",
    render: (e) => agencyLabel(e.agency),
    sortValue: (e) => e.agency,
  },
  {
    key: "supervisor",
    label: "Supervisor",
    render: (e) => e.supervisor?.fullName ?? "—",
    sortValue: (e) => e.supervisor?.fullName ?? null,
  },
  {
    key: "coordinator",
    label: "Coordinador",
    render: (e) => e.coordinator?.fullName ?? "—",
    sortValue: (e) => e.coordinator?.fullName ?? null,
  },
];

export function ParticipantsPanel() {
  /**
   * El mes lo manda la cabecera del panel y no un campo de aquí dentro: con
   * dos mandos para lo mismo en la misma pantalla, tarde o temprano dicen
   * meses distintos y nadie sabe cuál está filtrando la tabla.
   *
   * Llega como `AAAA-MM`, que es como lo dice el resto del sistema, y
   * `toFilters` lo parte en los dos campos que pide el padrón.
   */
  const { period } = useDashboardPeriod();
  const [controls, setControls] = useState<FilterControls>(EMPTY_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);

  /**
   * Se espera a que el usuario deje de escribir: cada tecla era una consulta.
   *
   * El retardo sigue aquí y no en Query porque es una decisión de la interfaz
   * --cuándo vale la pena preguntar-- y no de la caché.
   */
  const [applied, setApplied] = useState<FilterControls>(EMPTY_FILTERS);
  useEffect(() => {
    const id = setTimeout(() => {
      setApplied(controls);
    }, SEARCH_DELAY_MS);
    return () => {
      clearTimeout(id);
    };
  }, [controls]);

  // El mes no pasa por el retardo: se elige de un desplegable, no tecleando.
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const filters = useMemo(
    () => toFilters(applied, period, unlinkedOnly),
    [applied, period, unlinkedOnly],
  );
  // Solo el conteo de quienes están dados de alta y sin vincular al mes: no
  // trae filas (limit 1), y no depende de lo que se teclee en los filtros.
  const unlinkedCount = useInfiniteQuery(
    participantsQuery(toFilters(EMPTY_FILTERS, period, true), 1),
  );
  const unlinked = unlinkedCount.data?.pages[0]?.total ?? 0;
  const list = useInfiniteQuery(participantsQuery(filters));

  const detail = useQuery({
    ...participantQuery(openId ?? ""),
    enabled: openId !== null,
  });

  const rows = useMemo(
    () => list.data?.pages.flatMap((page) => page.participants) ?? [],
    [list.data],
  );
  const total = list.data?.pages[0]?.total ?? 0;
  // Cargar la página siguiente no es "buscar": la tabla y el recuento se
  // quedan donde estaban mientras llega.
  const loading = list.isFetching && !list.isFetchingNextPage;
  const error = list.error ?? detail.error;
  const open = detail.data ?? null;

  /**
   * Una búsqueda, cuando ya se sabe cuántos salieron: si había texto y qué
   * filtros, nunca lo escrito. La carga inicial no cuenta como búsqueda.
   */
  const searched = useRef<FilterControls | null>(null);
  useEffect(() => {
    if (applied !== EMPTY_FILTERS) searched.current = applied;
  }, [applied]);
  useEffect(() => {
    const done = searched.current;
    if (done === null || loading || list.data === undefined) return;
    searched.current = null;
    analytics.work.search({
      section: "participants",
      hasQuery: done.text.trim() !== "",
      query: done.text.trim(),
      resultCount: total,
      filters: [
        ...(done.positionId === ALL ? [] : ["position"]),
        ...(done.active === ALL ? [] : [`active:${done.active}`]),
      ],
    });
  }, [applied, loading, list.data, total]);

  // La ficha abierta, cuando llega: con sus datos, no solo el id.
  useEffect(() => {
    if (open === null) return;
    analytics.work.recordOpened({
      section: "participants",
      ...participantRecord(open),
    });
    // Una vez por participante abierto, no en cada refresco de la ficha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.id]);

  const set = (change: Partial<FilterControls>) => {
    setControls({ ...controls, ...change });
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* Sin titular: quién es esta pantalla lo dice la cabecera de arriba, y
          decirlo dos veces obliga a leer dos veces. */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {loading
              ? "Buscando…"
              : total === 1
                ? `1 participante ${unlinkedOnly ? "sin vincular" : "vinculado"} en `
                : `${total.toLocaleString("es")} participantes ${unlinkedOnly ? "sin vincular" : "vinculados"} en `}
            {!loading && <span className="font-mono">{period}</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Input
            className="w-56"
            placeholder="Nombre o identificador"
            value={controls.text}
            onChange={(event) => {
              set({ text: event.target.value });
            }}
          />
          <CatalogSelect
            catalog={POSITIONS}
            value={controls.positionId}
            allLabel="Todos los cargos"
            className="w-44"
            onChange={(positionId) => {
              set({ positionId });
            }}
          />

          <Select
            value={controls.active}
            onValueChange={(active) => {
              set({ active });
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Activos y no activos</SelectItem>
              <SelectItem value="active">Solo activos</SelectItem>
              <SelectItem value="inactive">Solo no activos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {error != null && (
        <Alert variant="destructive">
          <AlertTitle>No se pudo cargar</AlertTitle>
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      )}

      {unlinkedOnly ? (
        <Alert>
          <AlertTitle>Sin vincular a {period}</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            Estás viendo a quienes están dados de alta pero no participan en
            este mes.
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setUnlinkedOnly(false);
              }}
            >
              Ver los vinculados
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        unlinked > 0 && (
          <Alert>
            <AlertTitle>
              {unlinked === 1
                ? `1 participante sin vincular a ${period}`
                : `${unlinked.toLocaleString("es")} participantes sin vincular a ${period}`}
            </AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-3">
              Están dados de alta, pero todavía no participan en este mes.
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setUnlinkedOnly(true);
                }}
              >
                Verlos
              </Button>
            </AlertDescription>
          </Alert>
        )
      )}

      {!loading && rows.length === 0 && error == null && (
        <EmptyState icon={ParticipantsIcon}>
          {unlinkedOnly
            ? `Nadie está sin vincular a ${period}.`
            : `Nadie cumple el filtro en ${period}. Puedes darlos de alta hablando con el asistente en el Chat.`}
        </EmptyState>
      )}

      {rows.length > 0 && (
        <DataGrid
          columns={PARTICIPANT_COLUMNS}
          rows={rows}
          onSort={(column, direction) => {
            analytics.work.tableSorted({
              section: "participants",
              grid: "participants",
              column,
              direction,
            });
          }}
          onRowClick={(participant) => {
            setOpenId(participant.id);
          }}
        />
      )}

      {/* "Cargar más" en vez de páginas numeradas: el padrón se recorre de
          arriba abajo, y lo cargado se sigue pudiendo ordenar junto. */}
      {list.hasNextPage && (
        <Button
          variant="outline"
          className="self-center"
          disabled={list.isFetchingNextPage}
          onClick={() => {
            analytics.work.listMoreLoaded({
              section: "participants",
              loaded: rows.length,
            });
            void list.fetchNextPage();
          }}
        >
          {list.isFetchingNextPage
            ? "Cargando…"
            : `Cargar más (${(total - rows.length).toLocaleString("es")} restantes)`}
        </Button>
      )}

      {open !== null && (
        <ParticipantCard
          participant={open}
          period={period}
          onClose={() => {
            setOpenId(null);
          }}
        />
      )}
    </section>
  );
}

/** El estado, en la misma forma en la tabla y en la ficha. */
function StateTag({ active }: { readonly active: boolean }) {
  return (
    <Badge variant={active ? "secondary" : "outline"}>
      {activeLabel(active)}
    </Badge>
  );
}

/**
 * Un selector de catálogo, cargado de su listado.
 *
 * Es lo que sustituye al enum de roles que vivía escrito en este archivo: lo
 * que se puede elegir es lo que existe en el catálogo, no una lista que hay
 * que acordarse de actualizar en dos repositorios.
 */
function CatalogSelect({
  catalog,
  value,
  allLabel,
  className,
  onChange,
}: {
  readonly catalog: Catalog;
  readonly value: string;
  /** La opción que no filtra --o, en un formulario, la que deja el campo vacío. */
  readonly allLabel: string;
  readonly className?: string;
  onChange: (value: string) => void;
}) {
  const list = useQuery(catalogQuery(catalog));
  const entries = list.data?.entries ?? [];

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {entries.map((entry) => (
          <SelectItem key={entry.id} value={entry.id}>
            {entry.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** La ficha: la identidad arriba y el histórico de vinculaciones debajo. */
function ParticipantCard({
  participant,
  period,
  onClose,
}: {
  participant: ParticipantDetail;
  /** El mes que se está mirando: es con el que nace el formulario de vincular. */
  readonly period: string;
  onClose: () => void;
}) {
  const [enrolling, setEnrolling] = useState(false);
  const setActive = useSetParticipantActive(participant.id);
  const canWrite = useSession().can(PERMISSIONS.participantsWrite);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{participant.fullName}</DialogTitle>
          <DialogDescription className="font-mono">
            {participant.identifier}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between rounded-md border px-3 py-2">
          <div>
            <Label htmlFor="participant-active">
              {activeLabel(participant.active)}
            </Label>
            <p className="text-xs text-muted-foreground">
              Dar de baja no borra ni esconde nada: sus vinculaciones siguen
              donde estaban y sigue saliendo en las búsquedas.
            </p>
          </div>
          {/* Sin permiso se sigue viendo si está de alta: se deshabilita en
              vez de esconderse, porque el estado también es información. */}
          <Switch
            id="participant-active"
            checked={participant.active}
            disabled={!canWrite || setActive.isPending}
            onCheckedChange={(next) => {
              setActive.mutate(next, {
                onSuccess: () => {
                  analytics.work.participantActiveToggled({
                    section: "participants",
                    active: next,
                    ...participantRecord(participant),
                  });
                },
              });
            }}
          />
        </div>

        {setActive.isError && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(setActive.error)}</AlertDescription>
          </Alert>
        )}

        <dl className="grid gap-px overflow-hidden rounded-md border text-sm sm:grid-cols-3">
          <Fact label="Correo" value={participant.email ?? "—"} />
          <Fact label="Teléfono" value={participant.phone ?? "—"} />
          <Fact
            label="Alias"
            value={
              participant.aliases.length === 0
                ? "—"
                : participant.aliases.join(" · ")
            }
          />
        </dl>

        <div className="flex items-center justify-between gap-3">
          <h4 className="font-semibold">Vinculaciones</h4>
          {canWrite && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEnrolling(true);
              }}
            >
              <PlusIcon /> Vincular en un mes
            </Button>
          )}
        </div>

        <DataGrid
          columns={ENROLLMENT_COLUMNS}
          rows={participant.enrollments}
          empty="No participa en ningún mes. Eso es lo que significa no tener vinculación: no hay baja lógica."
        />

        {enrolling && (
          <EnrollDialog
            participant={participant}
            period={period}
            onClose={() => {
              setEnrolling(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Vincular a alguien con un mes.
 *
 * El cargo y la región se ELIGEN de sus catálogos; no hay manera de escribir
 * uno que no exista, que era justo lo que pasaba cuando la región era texto
 * libre y el rol un enum que el front repetía por su cuenta.
 *
 * El supervisor y el coordinador no se tocan aquí: exigen buscar a otra
 * persona del padrón y eso todavía se hace hablando con el agente. Los que ya
 * estén puestos se quedan como están.
 */
function EnrollDialog({
  participant,
  period,
  onClose,
}: {
  readonly participant: ParticipantDetail;
  readonly period: string;
  onClose: () => void;
}) {
  /**
   * El formulario nace con el mes que se está mirando, no con el de hoy: quien
   * abre la ficha desde el padrón de marzo casi siempre quiere vincular en
   * marzo. Perezoso, para no partir la cadena en cada render.
   */
  const [year, setYear] = useState(() =>
    String(fromPeriod(period)?.year ?? new Date().getFullYear()),
  );
  const [month, setMonth] = useState(() =>
    String(fromPeriod(period)?.month ?? new Date().getMonth() + 1),
  );
  const [positionId, setPositionId] = useState(ALL);
  const [regionId, setRegionId] = useState(ALL);
  const [agency, setAgency] = useState("");

  const enroll = useEnrollParticipant(participant.id);

  const parsedYear = parseYear(year);
  const parsedMonth = Number(month);

  /**
   * ¿Ya hay vinculación en ese mes?
   *
   * Si la hay, esto es una CORRECCIÓN y el backend exige decirlo. Se marca
   * solo, pero se avisa: pisar una carga no puede parecerse a un alta normal.
   */
  const existing = participant.enrollments.find(
    (enrollment) =>
      enrollment.year === parsedYear && enrollment.month === parsedMonth,
  );

  const canSave =
    parsedYear !== undefined && positionId !== ALL && !enroll.isPending;

  const save = () => {
    if (parsedYear === undefined || positionId === ALL) return;
    const trimmedAgency = agency.trim();

    enroll.mutate(
      {
        participantId: participant.id,
        year: parsedYear,
        month: parsedMonth,
        positionId,
        ...(regionId === ALL ? {} : { regionId }),
        ...(trimmedAgency === "" ? {} : { agency: trimmedAgency }),
        replace: existing !== undefined,
      },
      {
        onSuccess: () => {
          analytics.work.enrollmentSaved({
            section: "participants",
            mode: existing === undefined ? "create" : "update",
            ...participantRecord(participant),
            year: parsedYear,
            month: parsedMonth,
            ...(trimmedAgency === "" ? {} : { agency: trimmedAgency }),
            positionId,
            ...(regionId === ALL ? {} : { regionId }),
          });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <DialogHeader>
            <DialogTitle>Vincular en un mes</DialogTitle>
            <DialogDescription>
              {participant.fullName}. El supervisor y el coordinador se asignan
              hablando con el asistente; aquí no se tocan.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="enroll-year">Año</Label>
              <Input
                id="enroll-year"
                inputMode="numeric"
                value={year}
                aria-invalid={parsedYear === undefined}
                onChange={(event) => {
                  setYear(event.target.value);
                }}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="enroll-month">Mes</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger id="enroll-month" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Cargo</Label>
            <CatalogSelect
              catalog={POSITIONS}
              value={positionId}
              allLabel="Elige un cargo"
              className="w-full"
              onChange={setPositionId}
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Región</Label>
            <CatalogSelect
              catalog={REGIONS}
              value={regionId}
              allLabel="Sin región"
              className="w-full"
              onChange={setRegionId}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="enroll-agency">Agencia</Label>
            <Input
              id="enroll-agency"
              value={agency}
              placeholder="Vacío si es empleado directo"
              onChange={(event) => {
                setAgency(event.target.value);
              }}
            />
          </div>

          {existing !== undefined && (
            <Alert>
              <AlertTitle>Ya hay vinculación en ese mes</AlertTitle>
              <AlertDescription>
                Está como {existing.position.name}. Guardar la sustituye por
                esta.
              </AlertDescription>
            </Alert>
          )}

          {enroll.isError && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage(enroll.error)}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSave}>
              {enroll.isPending
                ? "Guardando…"
                : existing === undefined
                  ? "Vincular"
                  : "Sustituir"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-secondary/40 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  );
}
